import React, {useDeferredValue, useMemo, useRef} from 'react';
import {Editor, EditorTextChangeEvent} from 'primereact/editor';
import AdminFieldLabel from './AdminFieldLabel';

type RichTextFormat = 'bold' | 'italic' | 'underline' | 'script' | 'link' | 'list' | 'header' | 'indent';

const DEFAULT_FORMATS: RichTextFormat[] = ['bold', 'italic', 'underline', 'script'];

interface AdminRichTextAreaProps {
  label?: string;
  value: string;
  onChange: (e: string) => void;
  placeholder?: string;
  rows?: number;
  required?: boolean;
  name: string;
  id: string;
  className?: string;
  languageSwitcher?: React.ReactNode;
  formats?: RichTextFormat[];
  error?: string;
}

const stripHtmlTags = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const AdminRichTextArea: React.FC<AdminRichTextAreaProps> = ({
                                                               label,
                                                               value,
                                                               onChange,
                                                               required = false,
                                                               id,
                                                               className = '',
                                                               languageSwitcher,
                                                               formats = DEFAULT_FORMATS,
                                                               error
                                                             }) => {
  const editorRef = useRef<Editor>(null);

  const handleTextChange = (e: EditorTextChangeEvent): void => {
    const quill = editorRef.current?.getQuill();

    let html: string = (quill ? quill.getSemanticHTML() : e.htmlValue) || '';
    html = html.replace(/&nbsp;/g, ' ');

    onChange(html);
  };
  const renderHeader = () => {
    return (
      <>
        <span className="ql-formats">
          {formats.includes('bold') && <button className="ql-bold" aria-label="Bold"></button>}
          {formats.includes('italic') && <button className="ql-italic" aria-label="Italic"></button>}
          {formats.includes('underline') && <button className="ql-underline" aria-label="Underline"></button>}
          {formats.includes('script') && (
            <>
              <button className="ql-script" value="sub" aria-label="Subscript"></button>
              <button className="ql-script" value="super" aria-label="Superscript"></button>
            </>
          )}
        </span>
        <span className="ql-formats">
          {formats.includes('link') && <button className="ql-link" aria-label="Link"></button>}
          {formats.includes('list') && (
            <>
              <button className="ql-list" value="ordered" aria-label="Ordered List"></button>
              <button className="ql-list" value="bullet" aria-label="Unordered List"></button>
            </>
          )}
        </span>
        <span className="ql-formats">
          {formats.includes('header') && (
            <select className="ql-header" aria-label="Header">
              <option value="1">Title</option>
              <option value="2">Subtitle</option>
              <option value="3">Section</option>
              <option value="">Normal</option>
            </select>
          )}
        </span>
      </>
    );
  };

  const header = useMemo(() => renderHeader(), [formats]);

  const deferredValue = useDeferredValue(value);

  const processedValue = useMemo(() => {
    return value ? value.replace(/\n/g, '<br/>') : value;
  }, [value]);

  const characterCount = useMemo(() => {
    return stripHtmlTags(deferredValue || '').length;
  }, [deferredValue]);

  return (
    <div className={`${className} mb-4`}>
      {label && (
        <AdminFieldLabel
          htmlFor={id}
          label={label}
          required={required}
          languageSwitcher={languageSwitcher}
        />
      )}
      <div className={error ? 'border border-red-500 rounded' : ''}>
        <Editor
          ref={editorRef}
          id={id}
          value={processedValue}
          onTextChange={handleTextChange}
          headerTemplate={header}
          formats={formats}
          style={{height: '320px'}}
        />
      </div>
      <div className="text-xs text-gray-500 mt-1 text-right">
        {characterCount} characters
      </div>
      {error && <small className="block text-red-500 text-xs mt-1">{error}</small>}
    </div>
  );
};

export default AdminRichTextArea;

